import { useEffect, useRef, useState } from "react";
import { type BusinessView, useBusinesses } from "../../features/businesses/api.js";
import { useBusiness } from "../../lib/business.js";
import styles from "./BizSwitch.module.css";
import { Icon } from "./Icon.js";

/**
 * Business switcher (doc 01 §1.3) — the prototype's rich left-cluster dropdown:
 * colour dot + name + phone, menu headed "one company, two numbers". Replaces the
 * bare native <select>. `displayName` already resolves to phone_display ?? name on
 * the server, so we only render the phone sub-line when it actually differs from
 * the name (i.e. a real display number exists; staging without Meta data omits it).
 */
const phoneOf = (b: BusinessView): string | null =>
  b.displayName && b.displayName !== b.name ? b.displayName : null;

export function BizSwitch() {
  const { activeId, setActiveId } = useBusiness();
  const businesses = useBusinesses();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const list = businesses.data ?? [];
  const active = list.find((b) => b.id === activeId) ?? list[0];
  if (!active) return null;

  return (
    <div className={styles.switch} ref={ref}>
      <button
        type="button"
        className={styles.btn}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Active business"
      >
        <span
          className={styles.dot}
          style={{ background: `var(--${active.accent}, var(--heritage))` }}
        />
        <span className={styles.name}>{active.name}</span>
        {phoneOf(active) && <span className={styles.phone}>{phoneOf(active)}</span>}
        <Icon name="chevron" size={11} />
      </button>
      {open && (
        <div className={styles.menu} role="menu">
          <div className={styles.menuH}>Switch business · one company, two numbers</div>
          {list.map((b) => (
            <button
              key={b.id}
              type="button"
              role="menuitemradio"
              aria-checked={b.id === activeId}
              className={`${styles.opt} ${b.id === activeId ? styles.on : ""}`}
              onClick={() => {
                setActiveId(b.id);
                setOpen(false);
              }}
            >
              <span
                className={styles.dot}
                style={{ background: `var(--${b.accent}, var(--heritage))` }}
              />
              <span className={styles.optMain}>
                <span className={styles.optName}>{b.name}</span>
                {phoneOf(b) && <span className={styles.optPhone}>{phoneOf(b)}</span>}
              </span>
              {b.id === activeId && (
                <span className={styles.check} aria-hidden="true">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    role="img"
                    aria-label="Active"
                  >
                    <title>Active</title>
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
